import React, { useState, useEffect } from 'react';
import Fuse from 'fuse.js';
import { ref, push, set, onValue } from "firebase/database";
import { db, auth } from './firebase';
import { useNavigate } from 'react-router-dom';
import './App.css';

function ReagentList() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [cart, setCart] = useState([]);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);

  const [usageTime, setUsageTime] = useState('');
  const [purpose, setPurpose] = useState('');

  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('reagent_favorites')) || [];
    } catch { return []; }
  });

  const [isFavModalOpen, setIsFavModalOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const navigate = useNavigate();

  // Firebase에서 시약 데이터 실시간 로드
  useEffect(() => {
    const reagentRef = ref(db, 'reagents');
    const unsubscribe = onValue(reagentRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        setInventory(list);
      } else {
        setInventory([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const fuse = new Fuse(inventory, {
    includeScore: true,
    threshold: 0.3,
    keys: ['KORNAME', 'IUPACNAME', 'TRINAME', 'MOL_FORMULA']
  });

  const handleSearch = (e) => {
    const inputValue = e.target.value;
    setQuery(inputValue);
    if (inputValue.length > 0) {
      setResults(fuse.search(inputValue).slice(0, 50).map(res => res.item));
    } else {
      setResults([]);
    }
  };

  const toggleFavorite = (item) => {
    setFavorites(prev => {
      const exists = prev.some(f => f.id === item.id);
      const updated = exists ? prev.filter(f => f.id !== item.id) : [...prev, item];
      localStorage.setItem('reagent_favorites', JSON.stringify(updated));
      return updated;
    });
  };

  const addToCart = (item) => {
    if (cart.some(c => c.id === item.id)) {
      alert("이미 장바구니에 담긴 시약입니다!");
      return;
    }
    setCart([...cart, item]);
  };

  const removeFromCart = (id) => {
    const updated = cart.filter(item => item.id !== id);
    setCart(updated);
    if (updated.length === 0 && isCartModalOpen) closeCartModal();
  };

  const openCartModal = () => {
    if (cart.length === 0) {
      alert("장바구니가 비어있습니다. 시약을 먼저 담아주세요!");
      return;
    }
    setIsCartModalOpen(true);
    setUsageTime('');
    setPurpose('');
  };

  const closeCartModal = () => setIsCartModalOpen(false);

  const handleSubmit = () => {
    if (!usageTime || !purpose) {
      alert("사용 시간과 목적을 모두 입력해주세요!");
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      alert("로그인이 필요합니다!");
      navigate('/');
      return;
    }
    const batchId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
    const listRef = ref(db, 'applications');
    const promises = cart.map(item => {
      const newRef = push(listRef);
      return set(newRef, {
        reagentName: item.KORNAME || item.TRINAME || item.IUPACNAME,
        location: item.LOCATION || '위치 미상',
        usageTime,
        purpose,
        applicant: user.displayName || user.email,
        uid: user.uid,
        status: "대기중",
        timestamp: Date.now(),
        batchId,
      });
    });
    Promise.all(promises).then(() => {
      alert(`${cart.length}개의 시약 신청이 완료되었습니다! 🚀`);
      setCart([]);
      closeCartModal();
    }).catch(err => alert("전송 실패: " + err.message));
  };

  // NFPA 위험도 표시 헬퍼
  const getNFPAText = (item) => {
    const parts = [];
    if (item.NFPA_R !== '' && item.NFPA_R !== undefined) parts.push(`화재:${item.NFPA_R}`);
    if (item.NFPA_Y !== '' && item.NFPA_Y !== undefined) parts.push(`반응:${item.NFPA_Y}`);
    if (item.NFPA_B !== '' && item.NFPA_B !== undefined) parts.push(`건강:${item.NFPA_B}`);
    if (item.NFPA_W !== '' && item.NFPA_W !== undefined) parts.push(`특수:${item.NFPA_W}`);
    return parts.length > 0 ? parts.join(' / ') : null;
  };

  const renderTableBody = () => {
    if (inventory.length === 0) {
      return (
        <tr>
          <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
            📦 시약 데이터를 불러오는 중입니다...<br />
            <span style={{ fontSize: '0.85em' }}>데이터가 없다면 선생님께 문의하세요.</span>
          </td>
        </tr>
      );
    }
    if (results.length === 0 && query.length === 0) {
      return (
        <tr>
          <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
            🔍 시약 이름, 화학식, 영문명으로 검색해보세요.<br />
            <span style={{ fontSize: '0.85em', color: '#bbb' }}>총 {inventory.length}개의 시약이 등록되어 있습니다.</span>
          </td>
        </tr>
      );
    }
    if (results.length === 0 && query.length > 0) {
      return (
        <tr>
          <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            🔍 "{query}"에 대한 검색 결과가 없습니다.
          </td>
        </tr>
      );
    }
    return results.map((item) => {
      const isSelected = cart.some(c => c.id === item.id);
      const isFavorite = favorites.some(f => f.id === item.id);
      const displayName = item.KORNAME || item.TRINAME || item.IUPACNAME || '(이름 없음)';
      return (
        <tr
          key={item.id}
          style={{ backgroundColor: isSelected ? '#eaf6ff' : 'white', cursor: 'pointer' }}
          onClick={() => setDetailItem(item)}
        >
          <td style={{ padding: '10px', textAlign: 'center' }} onClick={(e) => { e.stopPropagation(); toggleFavorite(item); }}>
            <span style={{ fontSize: '1.2em', cursor: 'pointer' }}>{isFavorite ? '⭐' : '☆'}</span>
          </td>
          <td style={{ padding: '12px 15px' }}>
            <div className="col-name">{displayName}</div>
            {item.TRINAME && item.TRINAME !== displayName && (
              <span className="tag">{item.TRINAME}</span>
            )}
            {item.IUPACNAME && item.IUPACNAME !== displayName && (
              <span className="tag" style={{ marginLeft: '4px' }}>{item.IUPACNAME}</span>
            )}
          </td>
          <td style={{ padding: '12px 15px', color: '#e74c3c', fontFamily: 'monospace' }}>{item.MOL_FORMULA}</td>
          <td style={{ padding: '12px 15px' }} className="col-loc">{item.LOCATION || '-'}</td>
          <td style={{ padding: '12px 15px' }} onClick={(e) => e.stopPropagation()}>
            <button
              className="apply-btn"
              onClick={() => isSelected ? removeFromCart(item.id) : addToCart(item)}
              style={{ backgroundColor: isSelected ? '#e74c3c' : '#007BFF' }}
            >
              {isSelected ? '빼기' : '담기'}
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="container">
      <h1>🧪 시약 검색</h1>
      <p className="subtitle">
        시약명, 화학식, 영문명으로 검색하세요 &nbsp;|&nbsp;
        <button onClick={() => navigate('/mypage')} style={{ background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.95em' }}>
          📋 나의 신청 내역
        </button>
      </p>

      <div className="search-box">
        <input
          type="text"
          placeholder="예: 에탄올, NaOH, Acetic acid, CH3COOH..."
          value={query}
          onChange={handleSearch}
        />
      </div>

      {query.length > 0 && (
        <div className="status-bar">
          검색결과: {results.length}개 (전체 {inventory.length}개 중)
        </div>
      )}

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th style={{ padding: '10px', textAlign: 'center', width: '40px' }}>⭐</th>
              <th>물품명</th>
              <th>화학식</th>
              <th>위치</th>
              <th>장바구니</th>
            </tr>
          </thead>
          <tbody>{renderTableBody()}</tbody>
        </table>
      </div>

      {/* 플로팅 버튼 */}
      <div style={{ position: 'fixed', bottom: '30px', right: '30px', display: 'flex', gap: '12px', alignItems: 'center', zIndex: 900 }}>
        <div
          onClick={() => setIsFavModalOpen(true)}
          style={{ backgroundColor: '#f39c12', color: 'white', padding: '15px 25px', borderRadius: '30px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.1em', fontWeight: 'bold', transition: 'transform 0.2s' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          ⭐ 즐겨찾기 <span style={{ backgroundColor: '#e67e22', padding: '2px 10px', borderRadius: '20px', fontSize: '0.9em' }}>{favorites.length}</span>
        </div>
        <div
          onClick={openCartModal}
          style={{ backgroundColor: '#3498db', color: 'white', padding: '15px 25px', borderRadius: '30px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.1em', fontWeight: 'bold', transition: 'transform 0.2s' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          🛒 장바구니 신청 <span style={{ backgroundColor: '#e74c3c', padding: '2px 10px', borderRadius: '20px', fontSize: '0.9em' }}>{cart.length}</span>
        </div>
      </div>

      {/* 시약 상세 모달 */}
      {detailItem && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', width: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginTop: 0, borderBottom: '2px solid #3498db', paddingBottom: '10px', color: '#2c3e50' }}>🔬 시약 상세 정보</h2>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { label: '한글명', value: detailItem.KORNAME, bold: true },
                { label: '영문명 (IUPAC)', value: detailItem.IUPACNAME },
                { label: '관용명', value: detailItem.TRINAME },
                { label: '화학식', value: detailItem.MOL_FORMULA, color: '#e74c3c' },
                { label: '몰 질량 (g/mol)', value: detailItem.MOL_MASS },
                { label: 'CAS 번호', value: detailItem.CAS_NO },
                { label: '보관 위치', value: detailItem.LOCATION, color: '#d35400' },
                { label: '수량', value: detailItem.COUNT },
                { label: 'NFPA 위험도', value: getNFPAText(detailItem) },
              ].map(({ label, value, bold, color }) =>
                (value !== null && value !== undefined && value !== '') ? (
                  <div key={label} style={{ display: 'flex', gap: '10px', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px', alignItems: 'flex-start' }}>
                    <span style={{ minWidth: '130px', fontSize: '0.82em', color: '#7f8c8d', fontWeight: 'bold', paddingTop: '2px' }}>{label}</span>
                    <span style={{ fontWeight: bold ? 'bold' : 'normal', color: color || '#2c3e50', wordBreak: 'break-all' }}>{value}</span>
                  </div>
                ) : null
              )}
            </div>
            
            {/* 🌟 수정된 버튼 영역: MSDS 검색 버튼 추가 및 양쪽 정렬 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
              <a 
              href={`https://www.google.com/search?q=${detailItem.IUPACNAME || detailItem.KORNAME || detailItem.TRINAME}+MSDS`}                target="_blank" 
                rel="noopener noreferrer"
                style={{ padding: '10px 15px', backgroundColor: '#f39c12', color: 'white', borderRadius: '5px', textDecoration: 'none', fontWeight: 'bold', fontSize: '0.9em', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
              >
                📑 MSDS 검색
              </a>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => { cart.some(c => c.id === detailItem.id) ? removeFromCart(detailItem.id) : addToCart(detailItem); }}
                  style={{ padding: '10px 15px', backgroundColor: cart.some(c => c.id === detailItem.id) ? '#e74c3c' : '#2ecc71', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  {cart.some(c => c.id === detailItem.id) ? '🛒 장바구니에서 빼기' : '🛒 장바구니에 담기'}
                </button>
                <button onClick={() => setDetailItem(null)} style={{ padding: '10px 20px', cursor: 'pointer', border: 'none', borderRadius: '5px', backgroundColor: '#ecf0f1', fontWeight: 'bold' }}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 즐겨찾기 모달 */}
      {isFavModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', width: '400px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginTop: 0, borderBottom: '2px solid #f39c12', paddingBottom: '10px', color: '#2c3e50' }}>⭐ 즐겨찾기 목록</h2>
            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#7f8c8d' }}>즐겨찾기 시약 ({favorites.length}개)</p>
              {favorites.length === 0 ? (
                <p style={{ color: '#999', margin: 0 }}>즐겨찾기한 시약이 없습니다. ☆를 눌러 추가해보세요!</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em', lineHeight: '1.6' }}>
                  {favorites.map(item => {
                    const isInCart = cart.some(c => c.id === item.id);
                    return (
                      <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span>
                          <span style={{ fontWeight: 'bold' }}>{item.KORNAME || item.TRINAME}</span>
                          {item.MOL_FORMULA && <span style={{ color: '#999', fontSize: '0.85em', marginLeft: '6px' }}>({item.MOL_FORMULA})</span>}
                        </span>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <button
                            onClick={() => isInCart ? removeFromCart(item.id) : addToCart(item)}
                            style={{ padding: '4px 10px', backgroundColor: isInCart ? '#e74c3c' : '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.82em', fontWeight: 'bold' }}
                          >
                            {isInCart ? '빼기' : '담기'}
                          </button>
                          <button
                            onClick={() => toggleFavorite(item)}
                            style={{ background: 'none', border: 'none', color: '#f39c12', cursor: 'pointer', fontSize: '1.2em', padding: '0 2px' }}
                            title="즐겨찾기 해제"
                          >×</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setIsFavModalOpen(false)} style={{ padding: '10px 20px', cursor: 'pointer', border: 'none', borderRadius: '5px', backgroundColor: '#ecf0f1', fontWeight: 'bold' }}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 장바구니 모달 */}
      {isCartModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', width: '400px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginTop: 0, borderBottom: '2px solid #3498db', paddingBottom: '10px', color: '#2c3e50' }}>🛒 장바구니 일괄 신청</h2>
            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#7f8c8d' }}>선택된 시약 ({cart.length}개)</p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9em', lineHeight: '1.6' }}>
                {cart.map(item => (
                  <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span>{item.KORNAME || item.TRINAME} {item.MOL_FORMULA && <span style={{ color: '#999', fontSize: '0.85em' }}>({item.MOL_FORMULA})</span>}</span>
                    <button onClick={() => removeFromCart(item.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1.2em', padding: '0 5px' }}>×</button>
                  </li>
                ))}
              </ul>
              {cart.length === 0 && <p style={{ color: '#e74c3c', fontWeight: 'bold' }}>시약이 모두 삭제되었습니다. 창을 닫아주세요.</p>}
            </div>
            <div style={{ marginTop: '10px' }}>
              <label style={{ display: 'block', fontWeight: 'bold' }}>사용 예정 시간 <span style={{ color: '#999', fontSize: '0.8em', fontWeight: 'normal' }}>(모두 동일하게 적용됨)</span></label>
              <input type="text" value={usageTime} onChange={(e) => setUsageTime(e.target.value)} placeholder="예: 5월 12일 3교시" style={{ width: '100%', padding: '10px', boxSizing: 'border-box', marginTop: '5px', borderRadius: '5px', border: '1px solid #ccc' }} />
            </div>
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', fontWeight: 'bold' }}>사용 목적</label>
              <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="실험 내용을 적어주세요." style={{ width: '100%', padding: '10px', boxSizing: 'border-box', height: '80px', marginTop: '5px', borderRadius: '5px', border: '1px solid #ccc' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px' }}>
              <button onClick={closeCartModal} style={{ padding: '10px 20px', cursor: 'pointer', border: 'none', borderRadius: '5px', backgroundColor: '#ecf0f1' }}>닫기 / 계속 담기</button>
              <button
                onClick={handleSubmit}
                disabled={cart.length === 0}
                style={{ padding: '10px 20px', backgroundColor: cart.length === 0 ? '#bdc3c7' : '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: cart.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
              >한 번에 제출하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReagentList;